/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';

import Analytics from 'arbitrip-common/client/analytics';

import {
    LABEL_AVG_ROOM_NIGHT,
    boardLabelOf,
    buildDealViews,
    cancellationOf,
    isRoomOnly,
    priceLinesOf,
} from '../dealViewModel/dealViewModel';
import useBookDeal from '../dealViewModel/useBookDeal';
import {
    BoardIcon,
    COLOR_BORDER,
    COLOR_MUTED,
    COLOR_PRIMARY,
    COLOR_SECONDARY,
    COLOR_TEXT,
    CancellationIcon,
    CancellationText,
    CollapsiblePanel,
    DetailChips,
    LABEL_BOOK,
    MoreDetailsLink,
    PerkChip,
    PolicyMark,
    RoomImage,
    SupersededNote,
    SupplierTag,
    WithoutCardNote,
    buttonPrimaryStyle,
    sheetStyle,
} from '../dealViewModel/DealViewParts.react';

import './DealTableView.scss';

/**
 * DEV PANEL — the "Choose Your Room" TABLE design (the updated table view mock) over the cards
 * `dealViewModel.js` builds. One `.dtv-room` card per room card, one deal row per visible deal.
 */

const TITLE = 'Table view';
const LABEL_NO_CARDS = 'No cards to show';
const LABEL_SHOW_LESS = 'Show less';
const LABEL_SHOW_MORE_PREFIX = 'Show';
const LABEL_MORE_DEAL = 'more deal';
const LABEL_MORE_DEALS = 'more deals';
const MOBILE_VISIBLE_ROWS = 3;
const CLASS_ROW = 'dtv-drow';
const CLASS_ROW_HIDDEN_ON_MOBILE = 'dtv-drow dtv-mb-hide';
const IMAGE_HEIGHT = 160;
const IMAGE_RADIUS = 12;
const DEAL_ICON_SIZE = 20;
const COLOR_DEALS_PANEL_BG = '#F4F6F8';

const styles = {
    table: { display: 'flex', flexDirection: 'column', gap: '16px' },
    room: {
        display: 'flex',
        alignItems: 'stretch',
        border: `1px solid ${COLOR_BORDER}`,
        borderRadius: '16px',
        overflow: 'hidden',
        background: '#fff',
    },
    acc: {
        flex: '0 0 340px',
        width: '340px',
        boxSizing: 'border-box',
        background: '#fff',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    title: { fontSize: '21px', lineHeight: '26px', fontWeight: 700, color: COLOR_TEXT },
    subtitle: { fontSize: '12px', lineHeight: '16px', color: COLOR_MUTED, marginTop: '-8px' },
    perks: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' },
    perkChip: {
        display: 'inline-flex',
        alignItems: 'center',
        height: '24px',
        boxSizing: 'border-box',
        padding: '4px',
        borderRadius: '4px',
        boxShadow: `0 0 0 1px ${COLOR_BORDER}`,
        whiteSpace: 'nowrap',
    },
    moreDetails: { padding: '8px 0' },
    dwrap: {
        flex: '1 1 0',
        minWidth: 0,
        boxSizing: 'border-box',
        background: COLOR_DEALS_PANEL_BG,
        borderLeft: `1px solid ${COLOR_BORDER}`,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    deals: {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        background: '#fff',
        border: `1px solid ${COLOR_BORDER}`,
        overflow: 'hidden',
    },
    drow: {
        display: 'flex',
        alignItems: 'stretch',
        borderTop: `1px solid ${COLOR_BORDER}`,
        marginTop: '-1px',
        background: '#fff',
    },
    deal: {
        flex: '1 1 0',
        minWidth: '160px',
        boxSizing: 'border-box',
        borderRight: `1px solid ${COLOR_BORDER}`,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '8px',
    },
    dealLine: { display: 'flex', alignItems: 'center', gap: '8px', minHeight: '20px', alignSelf: 'stretch' },
    board: { fontSize: '13px', lineHeight: '16px', color: COLOR_TEXT },
    boardWithMeal: { fontSize: '13px', lineHeight: '16px', fontWeight: 700, color: COLOR_TEXT },
    price: {
        flex: '0 1 378px',
        minWidth: 0,
        boxSizing: 'border-box',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        justifyContent: 'space-between',
    },
    priceLines: {
        flex: '1 1 auto',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        alignItems: 'flex-start',
    },
    total: { fontSize: '21px', lineHeight: '26px', fontWeight: 700, color: COLOR_TEXT, whiteSpace: 'nowrap' },
    nights: { fontSize: '13px', lineHeight: '16px', color: COLOR_TEXT, whiteSpace: 'nowrap' },
    avgRow: { display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' },
    avg: { fontSize: '13px', lineHeight: '16px', color: COLOR_SECONDARY, whiteSpace: 'nowrap' },
    cta: {
        ...buttonPrimaryStyle,
        flexShrink: 0,
        width: '90px',
        height: '42px',
        boxSizing: 'border-box',
        padding: '12px 18px',
        fontSize: '14px',
        lineHeight: '18px',
        whiteSpace: 'nowrap',
    },
    more: {
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        height: '44px',
        border: `1px solid ${COLOR_PRIMARY}`,
        borderRadius: '8px',
        background: '#fff',
        fontFamily: 'inherit',
        fontSize: '15px',
        fontWeight: 700,
        color: COLOR_PRIMARY,
        cursor: 'pointer',
    },
    empty: { fontSize: '12px', color: COLOR_MUTED },
};

function showMoreLabel(hiddenCount) {
    return `${LABEL_SHOW_MORE_PREFIX} ${hiddenCount} ${hiddenCount === 1 ? LABEL_MORE_DEAL : LABEL_MORE_DEALS}`;
}

/** The two deal lines of the mock: cancellation (with the supplier tag at the end), then board. */
const TableDealLines = ({ deal }) => {
    const cancellation = cancellationOf(deal);
    const roomOnly = isRoomOnly(deal);
    return (
        <>
            <div style={styles.dealLine}>
                <CancellationIcon cancellation={cancellation} size={DEAL_ICON_SIZE} />
                <CancellationText
                    cancellation={cancellation}
                    fontSize="13px"
                    lineHeight="16px"
                    color={cancellation.nonRefundable ? COLOR_TEXT : undefined}
                />
                <SupplierTag deal={deal} />
            </div>
            <div style={styles.dealLine}>
                <BoardIcon deal={deal} size={DEAL_ICON_SIZE} />
                <span style={roomOnly ? styles.board : styles.boardWithMeal}>{boardLabelOf(deal)}</span>
            </div>
        </>
    );
};

const TableDealRow = ({ deal, profile, hiddenOnMobile, onBook }) => {
    const lines = priceLinesOf(deal, profile);
    return (
        <div className={hiddenOnMobile ? CLASS_ROW_HIDDEN_ON_MOBILE : CLASS_ROW} style={styles.drow}>
            <div className="dtv-deal" style={styles.deal}>
                <TableDealLines deal={deal} />
            </div>
            <div className="dtv-price" style={styles.price}>
                <div className="dtv-pcol" style={styles.priceLines}>
                    <span style={styles.total}>{lines.total}</span>
                    <span style={styles.nights}>{lines.nightsLine}</span>
                    <div style={styles.avgRow}>
                        <span style={styles.avg}>
                            {lines.avg} {LABEL_AVG_ROOM_NIGHT}
                        </span>
                        <PolicyMark policy={lines.policy} />
                    </div>
                </div>
                <button type="button" className="dtv-cta" style={styles.cta} onClick={() => onBook(deal)}>
                    {LABEL_BOOK}
                </button>
            </div>
        </div>
    );
};

/** The perk chips of `PerkLines`, boxed and stacked as the mock draws them, then the "More details" link. */
const TablePerks = ({ details, onMoreDetails }) => (
    <div style={styles.perks}>
        {[].concat(...details.perkLines).map((perk, index) => (
            <span key={`${index}-${perk}`} style={styles.perkChip}>
                <PerkChip name={perk} gap="4px" />
            </span>
        ))}
        <div style={styles.moreDetails}>
            <MoreDetailsLink onClick={onMoreDetails} disabled={!details.hasDetails} />
        </div>
    </div>
);

const TableRoomRow = ({ card, profile, onBook, onMoreDetails }) => {
    const [showAll, setShowAll] = useState(false);
    const { details } = card;
    const hiddenCount = card.deals.length - MOBILE_VISIBLE_ROWS;

    return (
        <div className="dtv-room" style={styles.room}>
            <div className="dtv-acc" style={styles.acc}>
                <RoomImage details={details} title={card.title} height={IMAGE_HEIGHT} radius={IMAGE_RADIUS} navigable />
                <div style={styles.title} title={card.cardId}>
                    {card.title}
                </div>
                {card.subtitleLine && <div style={styles.subtitle}>{card.subtitleLine}</div>}
                {details.hasDetails && <DetailChips details={details} />}
                {details.hasDetails && <TablePerks details={details} onMoreDetails={() => onMoreDetails(card)} />}
                <SupersededNote count={card.supersededCount} alternatives={card.alternativesKept} />
            </div>

            <div className="dtv-dwrap" style={styles.dwrap}>
                <div className="dtv-deals" style={styles.deals}>
                    {card.deals.map((deal, index) => (
                        <TableDealRow
                            key={deal.id}
                            deal={deal}
                            profile={profile}
                            hiddenOnMobile={!showAll && index >= MOBILE_VISIBLE_ROWS}
                            onBook={onBook}
                        />
                    ))}
                </div>
                {hiddenCount > 0 && (
                    <button
                        type="button"
                        className="dtv-mb-more"
                        style={styles.more}
                        onClick={() => setShowAll(!showAll)}
                    >
                        {showAll ? LABEL_SHOW_LESS : showMoreLabel(hiddenCount)}
                    </button>
                )}
            </div>
        </div>
    );
};

const DealTableView = ({ deals, hotel, profile, reservationManipulation, onRoomDetails }) => {
    const { cards, withoutCardCount } = useMemo(() => buildDealViews(deals, hotel), [deals, hotel]);
    const bookDeal = useBookDeal({ hotel, profile, reservationManipulation });
    const openDetails = (card) => onRoomDetails(card.details.room, card.title, Analytics.actions.views.roomInfoDialog);

    return (
        <CollapsiblePanel title={TITLE} count={cards.length}>
            <div className="dtv-root" style={sheetStyle}>
                {cards.length === 0 ? (
                    <div style={styles.empty}>{LABEL_NO_CARDS}</div>
                ) : (
                    <div className="dtv-table" style={styles.table}>
                        {cards.map((card) => (
                            <TableRoomRow
                                key={card.cardId}
                                card={card}
                                profile={profile}
                                onBook={bookDeal}
                                onMoreDetails={openDetails}
                            />
                        ))}
                    </div>
                )}
                <WithoutCardNote count={withoutCardCount} />
            </div>
        </CollapsiblePanel>
    );
};

export default DealTableView;
